import type { SyncToEhrAction } from "@utils/ehrActions";
import { useEffect, useReducer, useRef } from "react";

import { ApiReportEhrSyncChoices, EhrConnectionStatus } from "@api/graphql/types-and-hooks";

type EhrLoginOutcome = "existing" | "fresh" | null;

interface State {
  isEHRLogin: boolean;
  isEHRLoginSuccess: boolean;
  ehrLoginOutcome: EhrLoginOutcome;
  pendingSyncAfterLogin: boolean;
}

type Action =
  | { type: "SET_EHR_LOGIN"; payload: boolean }
  | { type: "SET_EHR_LOGIN_SUCCESS"; payload: boolean }
  | { type: "SET_EHR_LOGIN_OUTCOME"; payload: EhrLoginOutcome }
  | { type: "SET_PENDING_SYNC_AFTER_LOGIN"; payload: boolean }
  | { type: "RESET_ON_PENDING_SYNC" }
  | { type: "RESET_ON_FRESH_OR_EXISTING_LOGIN" };

const initialState: State = {
  isEHRLogin: false,
  isEHRLoginSuccess: false,
  ehrLoginOutcome: null,
  pendingSyncAfterLogin: false,
};

function stateReducer(state: State, action: Action): State {
  switch (action.type) {
    case "SET_EHR_LOGIN":
      return { ...state, isEHRLogin: action.payload };
    case "SET_EHR_LOGIN_SUCCESS":
      return { ...state, isEHRLoginSuccess: action.payload };
    case "SET_EHR_LOGIN_OUTCOME":
      return { ...state, ehrLoginOutcome: action.payload };
    case "SET_PENDING_SYNC_AFTER_LOGIN":
      return { ...state, pendingSyncAfterLogin: action.payload };
    case "RESET_ON_PENDING_SYNC":
      return {
        ...state,
        isEHRLoginSuccess: false,
        pendingSyncAfterLogin: false,
        isEHRLogin: false,
        ehrLoginOutcome: null,
      };
    case "RESET_ON_FRESH_OR_EXISTING_LOGIN":
      return {
        ...state,
        isEHRLoginSuccess: false,
        isEHRLogin: false,
        ehrLoginOutcome: null,
      };
  }
}

export interface UseEhrSyncLoginFlowOptions {
  /** When false the credential-failure auto-open is suppressed (the page has no per-provider login for this EHR). */
  enabled: boolean;
  /** Starts the write-back. Called directly after a "log in, then sync" login. */
  syncGoalsToEhr: () => void;
  openSyncConfirm: () => void;
  openOrgContact: () => void;
  /**
   * Runs after a fresh credential login, before the confirm dialog. Resolve
   * false to skip the confirm (e.g. it failed and already notified the user).
   * Omit to open the confirm straight away. Must not reject — resolve false
   * on failure (callers notify the user themselves).
   */
  onFreshLogin?: () => Promise<boolean>;
  ehrSync: ApiReportEhrSyncChoices | null | undefined;
  ehrConnectionStatus: EhrConnectionStatus | null | undefined;
  isCRDataWarehouse: boolean;
  isOrgUsernamePassword: boolean;
}

/**
 * Per-provider EHR login handling for the "Sync to EHR" buttons: dispatches the
 * decideSyncToEhrAction outcome, drives the EHRLoginDialog, and re-opens the
 * login when a sync failed on invalid credentials. Shared by the IA and RA
 * report pages.
 */
export const useEhrSyncLoginFlow = ({
  enabled,
  syncGoalsToEhr,
  openSyncConfirm,
  openOrgContact,
  onFreshLogin,
  ehrSync,
  ehrConnectionStatus,
  isCRDataWarehouse,
  isOrgUsernamePassword,
}: UseEhrSyncLoginFlowOptions) => {
  const [state, dispatch] = useReducer(stateReducer, initialState);
  const loginFailureHandledRef = useRef(false);
  const loginSuccessProcessedRef = useRef(false);

  // loginSuccessProcessedRef is the actual re-fire guard: it latches true the
  // first time this effect sees isEHRLoginSuccess and short-circuits any
  // further runs (e.g. a re-render with new callback identities) until the
  // flag flips back to false, so the confirm/sync can't double-fire.
  useEffect(() => {
    if (!state.isEHRLoginSuccess) {
      loginSuccessProcessedRef.current = false;
      return;
    }
    if (loginSuccessProcessedRef.current) return;
    loginSuccessProcessedRef.current = true;

    // User clicked sync before logging in: sync now, skip the confirm dialog.
    if (state.pendingSyncAfterLogin) {
      dispatch({ type: "RESET_ON_PENDING_SYNC" });
      syncGoalsToEhr();
      return;
    }
    if (state.ehrLoginOutcome === "fresh" && onFreshLogin) {
      onFreshLogin()
        .then((shouldConfirm) => {
          if (shouldConfirm) openSyncConfirm();
        })
        .catch(() => {
          // Contractually onFreshLogin must not reject — it resolves false on
          // failure and notifies the user itself (see the option's doc comment).
          // This catch exists only so the promise is never left unhandled.
        })
        .finally(() => {
          dispatch({ type: "RESET_ON_FRESH_OR_EXISTING_LOGIN" });
        });
      return;
    }
    if (state.ehrLoginOutcome === "existing" || state.ehrLoginOutcome === "fresh") {
      openSyncConfirm();
      dispatch({ type: "RESET_ON_FRESH_OR_EXISTING_LOGIN" });
    }
  }, [
    state.isEHRLoginSuccess,
    state.ehrLoginOutcome,
    state.pendingSyncAfterLogin,
    syncGoalsToEhr,
    onFreshLogin,
    openSyncConfirm,
  ]);

  // When a sync fails due to credential failure, auto-open the login dialog so
  // the user can re-enter credentials. After re-login the confirm dialog appears
  // (via the login-success effect) so the user still explicitly confirms the retry.
  useEffect(() => {
    if (
      enabled &&
      ehrSync === ApiReportEhrSyncChoices.Failure &&
      ehrConnectionStatus === EhrConnectionStatus.CredentialsFailed &&
      !loginFailureHandledRef.current &&
      !isCRDataWarehouse &&
      !isOrgUsernamePassword
    ) {
      loginFailureHandledRef.current = true;
      dispatch({ type: "SET_EHR_LOGIN", payload: true });
    }
    if (ehrSync !== ApiReportEhrSyncChoices.Failure) {
      loginFailureHandledRef.current = false;
    }
  }, [enabled, ehrSync, ehrConnectionStatus, isCRDataWarehouse, isOrgUsernamePassword]);

  const openLogin = (pendingSync: boolean) => {
    dispatch({ type: "SET_EHR_LOGIN_SUCCESS", payload: false });
    dispatch({ type: "SET_EHR_LOGIN_OUTCOME", payload: null });
    dispatch({ type: "SET_PENDING_SYNC_AFTER_LOGIN", payload: pendingSync });
    dispatch({ type: "SET_EHR_LOGIN", payload: true });
  };

  const dispatchSyncAction = (action: SyncToEhrAction) => {
    switch (action) {
      case "open-sync":
        openSyncConfirm();
        return;
      case "open-org-contact":
        // Org EHR genuinely not set up: the provider has no credentials of their
        // own, so direct them to their organization administrator.
        openOrgContact();
        return;
      case "open-login":
        // Known invalid credentials: confirm dialog appears after re-login.
        openLogin(false);
        return;
      case "open-login-pending-sync":
        openLogin(true);
        return;
    }
  };

  return {
    isEHRLogin: state.isEHRLogin,
    dispatchSyncAction,
    loginDialogProps: {
      open: state.isEHRLogin,
      setOpen: (open: boolean) => {
        dispatch({ type: "SET_EHR_LOGIN", payload: open });
        if (!open) dispatch({ type: "SET_PENDING_SYNC_AFTER_LOGIN", payload: false });
      },
      setSuccessLogin: (success: boolean) => {
        dispatch({ type: "SET_EHR_LOGIN_SUCCESS", payload: success });
      },
      setLoginOutcome: (outcome: EhrLoginOutcome) => {
        dispatch({ type: "SET_EHR_LOGIN_OUTCOME", payload: outcome });
      },
    },
  };
};
